import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  Stack,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { formatInTimeZone } from "date-fns-tz";
import { useDispatch, useSelector } from "react-redux";
import {
  sendTicketEmailToAttendees,
  updateTicketOrderFormAnswer,
} from "../../../redux/features/adminEventsSlice";
import { fetchAttendeeMetrics } from "../../../redux/features/attendeesSlice";
import ConfirmationDialog from "../../ConfirmationDialog";
import OrderFormFieldValueEdit from "./OrderFormFieldValueEdit";

const formatCents = (cents) => {
  if (cents == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((cents || 0) / 100);
};

const attendeeIdFromEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : "";

const TicketDetailsModalContents = ({
  event,
  ticket,
  timezone,
  handleClose,
  setEditingOrderFormKey,
  editingOrderFormKey,
  onOrderIdClicked,
  onOrderIdFilterClick,
}) => {
  const dispatch = useDispatch();

  const [confirmAttendeeSendTicketId, setConfirmAttendeeSendTicketId] =
    useState(false);

  const organizationId = event?.organizationId;
  const userIsAdmin = Boolean(event?.userIsAdmin);
  const attendeeEmail = ticket
    ? ticket.displayEmail ||
      (ticket.orderFormFields || []).find((f) => f.key === "email")?.value
    : "";
  const attendeeId = attendeeEmail ? attendeeIdFromEmail(attendeeEmail) : "";
  const showAttendeeLink =
    userIsAdmin && Boolean(organizationId) && Boolean(attendeeId);

  const attendeeMetrics = useSelector((state) =>
    showAttendeeLink && organizationId && attendeeId
      ? state.attendees?.attendeeMetrics?.[organizationId]?.[attendeeId]
      : null,
  );

  useEffect(() => {
    if (showAttendeeLink && organizationId && attendeeId) {
      dispatch(
        fetchAttendeeMetrics({ organizationId, attendeeId }),
      );
    }
  }, [dispatch, showAttendeeLink, organizationId, attendeeId]);

  if (!ticket) {
    return null;
  }

  const {
    orderFormFields: eventOrderFormFields,
  } = event;
  const {
    id: ticketId,
    name,
    orderFormFields,
    purchaserOrderFormFields,
    createdAt,
    source,
    appliedPromoCode,
    promoCode,
    affiliate: affiliateName,
    order,
  } = ticket;
  const orderId = order?.id;
  const { displayName: manualEntryUser, type } = source || {};
  const datePattern = "M/d/yyyy h:mm a z";
  const dateText = formatInTimeZone(createdAt.toDate(), timezone, datePattern);
  let sourceText;
  if (Boolean(type)) {
    switch (type) {
      case "manual_user":
        sourceText = "Manually Entered";
        break;
      case "retail_web":
        sourceText = "Web";
        break;
      default:
        sourceText = "Unknown";
        break;
    }
  }

  const handleUpdate = ({ value, isAttendee, orderFormFieldKey }) => {
    dispatch(
      updateTicketOrderFormAnswer({
        eventId: event.id,
        ticket,
        orderFormFieldKey,
        isAttendee,
        value,
      }),
    );
  };

  return (
    <Box>
      <ConfirmationDialog
        open={Boolean(confirmAttendeeSendTicketId)}
        title={"Send email?"}
        message={"Email ticket to attendee?"}
        handleCancel={() => setConfirmAttendeeSendTicketId(null)}
        handleConfirm={async () => {
          dispatch(
            sendTicketEmailToAttendees({
              eventId: event.id,
              ticketIds: [confirmAttendeeSendTicketId],
            }),
          );
          setConfirmAttendeeSendTicketId(null);
        }}
      />
      <Box display={"flex"}>
        <Typography flexGrow={"1"} variant="h6">
          {name}
        </Typography>
        <Tooltip title="Close">
          <IconButton
            onClick={() => {
              handleClose();
            }}
          >
            <Close />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="subtitle2" color="primary.lightText">
        {dateText}
      </Typography>
      {showAttendeeLink && (
        <Box marginTop={"20px"}>
          <Typography
            component={RouterLink}
            to={`/organizations/${organizationId}/attendees/${attendeeId}`}
            sx={{
              color: "primary.main",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View attendee profile
          </Typography>
          {attendeeMetrics && (
            <Box
              sx={{ mt: 0.5 }}
              display="flex"
              flexDirection="column"
              gap={0.25}
            >
              <Typography variant="body2" color="primary.lightText">
                Events attended: {attendeeMetrics.eventCountTotal ?? 0}
              </Typography>
              <Typography variant="body2" color="primary.lightText">
                Total spend: {formatCents(attendeeMetrics.totalSpendUsdCents)}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Box display={"flex"} alignItems="center" gap={"5px"} marginTop={"20px"}>
        <Typography color={"primary.lightText"}>Order ID:</Typography>
        <Typography
          sx={{
            cursor:
              Boolean(onOrderIdClicked) && userIsAdmin ? "pointer" : "default",
          }}
          color={
            Boolean(onOrderIdClicked) && userIsAdmin ? "primary" : ""
          }
          onClick={() => {
            if (Boolean(onOrderIdClicked) && userIsAdmin) {
              onOrderIdClicked(orderId);
              handleClose();
            }
          }}
        >
          {orderId}
        </Typography>
        {Boolean(onOrderIdFilterClick) && (
          <Tooltip title="Filter tickets by this order">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                onOrderIdFilterClick(orderId);
                handleClose();
              }}
              aria-label="Filter by order"
              data-testid="order-id-filter-button"
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {Boolean(sourceText) && (
        <Box>
          <Box display={"flex"} gap={"5px"}>
            <Typography color={"primary.lightText"}>Source:</Typography>
            <Typography>{sourceText}</Typography>
          </Box>

          {Boolean(manualEntryUser) && (
            <Box display={"flex"} gap={"5px"}>
              <Typography color={"primary.lightText"}>Entered By:</Typography>
              <Typography>{manualEntryUser}</Typography>
            </Box>
          )}
        </Box>
      )}
      {appliedPromoCode && (
        <Box display={"flex"} gap={"5px"} marginTop={"20px"}>
          <Typography color={"primary.lightText"}>Promo Code:</Typography>
          <Typography>{promoCode}</Typography>
        </Box>
      )}
      {Boolean(affiliateName) && (
        <Box display={"flex"} gap={"5px"} marginTop={"20px"}>
          <Typography color={"primary.lightText"}>Affiliate:</Typography>
          <Typography>{affiliateName}</Typography>
        </Box>
      )}
      <Button
        sx={{ marginTop: "20px" }}
        variant="outlined"
        onClick={() => {
          setConfirmAttendeeSendTicketId(ticketId);
        }}
      >
        Email Ticket to Attendee
      </Button>
      <Typography sx={{ marginTop: "20px", fontWeight: "bold" }}>
        Attendee Order Forms
      </Typography>
      {orderFormFields.length === 0 && <Typography>None</Typography>}
      <Stack divider={<Divider />}>
        {orderFormFields.map((orderFormField, index) => {
          const { key } = orderFormField;
          const eventOrderFormField = eventOrderFormFields.find(
            ({ key: eventOrderFormFieldKey }) => eventOrderFormFieldKey === key,
          );
          const { allowScannersToUpdate } = eventOrderFormField || {};
          const canEdit = Boolean(userIsAdmin || allowScannersToUpdate);
          return (
            <OrderFormFieldValueEdit
              {...{
                canEdit,
                key: `ticket-form-edit-${index}`,
                ticket: true,
                orderFormField,
                eventOrderFormField,
                index,
                editing: editingOrderFormKey === `attendee-${key}`,
                setEditing: (editing) => {
                  setEditingOrderFormKey(editing ? `attendee-${key}` : null);
                },
                handleUpdate: (value) => {
                  handleUpdate({
                    value,
                    isAttendee: true,
                    orderFormFieldKey: key,
                  });
                },
              }}
            />
          );
        })}
        <Typography sx={{ marginTop: "20px", fontWeight: "bold" }}>
          Purchaser Order Forms
        </Typography>
        {(purchaserOrderFormFields || []).length === 0 && (
          <Typography>None</Typography>
        )}
        <Stack divider={<Divider />}>
          {(purchaserOrderFormFields || []).map((orderFormField, index) => {
            const { key } = orderFormField;
            const eventOrderFormField = eventOrderFormFields.find(
              ({ key: eventOrderFormFieldKey }) =>
                eventOrderFormFieldKey === key,
            );
            const { allowScannersToUpdate } = eventOrderFormField || {};
            const canEdit = Boolean(userIsAdmin || allowScannersToUpdate);
            return (
              <OrderFormFieldValueEdit
                {...{
                  canEdit,
                  key: `purchaser-form-edit-${key}`,
                  ticket: false,
                  orderFormField,
                  eventOrderFormField,
                  index,
                  editing: editingOrderFormKey === `purchaser-${key}`,
                  setEditing: (editing) => {
                    setEditingOrderFormKey(editing ? `purchaser-${key}` : null);
                  },
                  handleUpdate: (value) => {
                    handleUpdate({
                      value,
                      isAttendee: false,
                      orderFormFieldKey: key,
                    });
                  },
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
};

export default TicketDetailsModalContents;
